import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { 
  MessageCircle, X, Send, Loader2, Users, Archive, Calendar, 
  History, Plus, Hash, User, Check
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

// Available colors/icons for rooms
const ROOM_COLORS = [
  { id: 'blue', icon: '💬', color: 'bg-blue-500', name: 'Niebieski' },
  { id: 'red', icon: '🔴', color: 'bg-red-500', name: 'Czerwony' },
  { id: 'green', icon: '🟢', color: 'bg-emerald-500', name: 'Zielony' },
  { id: 'yellow', icon: '🟡', color: 'bg-yellow-500', name: 'Żółty' },
  { id: 'purple', icon: '🟣', color: 'bg-purple-500', name: 'Fioletowy' },
  { id: 'orange', icon: '🟠', color: 'bg-orange-500', name: 'Pomarańczowy' },
  { id: 'team', icon: '👥', color: 'bg-navy-600', name: 'Zespół' },
  { id: 'plane', icon: '✈️', color: 'bg-sky-500', name: 'Podróże' },
  { id: 'money', icon: '💰', color: 'bg-amber-500', name: 'Finanse' },
  { id: 'star', icon: '⭐', color: 'bg-gold-500', name: 'Ważne' },
  { id: 'heart', icon: '❤️', color: 'bg-pink-500', name: 'Serce' },
  { id: 'fire', icon: '🔥', color: 'bg-red-600', name: 'Pilne' },
];

const ChatWidget = () => {
  const { user, isAdmin } = useAuth();
  const [chatOpen, setChatOpen] = useState(false);
  const [rooms, setRooms] = useState({ channels: [], direct_messages: [], users: [] });
  const [currentRoom, setCurrentRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  // New room dialog state
  const [showNewRoomDialog, setShowNewRoomDialog] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomColor, setNewRoomColor] = useState(ROOM_COLORS[0]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [creatingRoom, setCreatingRoom] = useState(false);

  const currentDate = new Date().toISOString().split('T')[0];

  // Fetch rooms
  const fetchRooms = useCallback(async () => {
    if (!user) return;
    try {
      const response = await api.get('/chat/rooms');
      setRooms(response.data);
      
      if (!currentRoom && response.data.channels.length > 0) {
        setCurrentRoom(response.data.channels[0]);
      }
    } catch (err) {
      console.error('Error fetching rooms:', err);
    }
  }, [user, currentRoom]);

  // Fetch messages for current room
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchMessages = useCallback(async (append = false) => {
    if (!user || !currentRoom) return;
    try {
      const skip = append ? messages.length : 0;
      const response = await api.get(`/chat/messages/${currentRoom.id}?date=${currentDate}&limit=50&skip=${skip}`);
      const data = response.data || {};
      const newMsgs = data.messages || [];
      setHasMore(data.has_more || false);
      
      if (append) {
        setMessages(prev => [...newMsgs, ...prev]);
      } else {
        setMessages(newMsgs);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user, currentRoom, currentDate, messages.length]);

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    fetchMessages(true);
  }, [fetchMessages]);

  // Initial load
  useEffect(() => {
    if (user) fetchRooms();
  }, [user]);

  // WebSocket connection for real-time messages with polling fallback
  const [wsConnected, setWsConnected] = useState(false);
  const pollIntervalRef = useRef(null);

  const connectWS = useCallback((roomId) => {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    if (!roomId || !user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const base = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    const url = `${base}/ws/chat/${roomId}?token=${token}`;
    try {
      const ws = new WebSocket(url);
      const timeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close();
          setWsConnected(false);
        }
      }, 5000);
      ws.onopen = () => {
        clearTimeout(timeout);
        setWsConnected(true);
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          setMessages(prev => {
            if (prev.some(m => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          if (!chatOpen && msg.sender_id !== user.id) {
            setUnreadCount(prev => prev + 1);
          }
        } catch {}
      };
      ws.onclose = () => {
        clearTimeout(timeout);
        wsRef.current = null;
        setWsConnected(false);
      };
      ws.onerror = () => { clearTimeout(timeout); ws.close(); };
      wsRef.current = ws;
    } catch { setWsConnected(false); }
  }, [user, chatOpen]);

  // Connect WebSocket when room changes
  useEffect(() => {
    if (currentRoom && chatOpen) {
      connectWS(currentRoom.id);
    }
    return () => {
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [currentRoom?.id, chatOpen, connectWS]);

  // Polling fallback when WebSocket not connected
  useEffect(() => {
    if (chatOpen && currentRoom && !wsConnected) {
      pollIntervalRef.current = setInterval(fetchMessages, 5000);
      return () => clearInterval(pollIntervalRef.current);
    }
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
  }, [chatOpen, currentRoom, wsConnected, fetchMessages]);

  // Load messages when room changes
  useEffect(() => {
    if (currentRoom) {
      setLoading(true);
      fetchMessages();
    }
  }, [currentRoom]);

  // Auto-scroll
  useEffect(() => {
    if (chatOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, chatOpen]);

  // Focus input
  useEffect(() => {
    if (chatOpen && inputRef.current && !showNewRoomDialog) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [chatOpen, currentRoom, showNewRoomDialog]);

  // Clear unread on open
  useEffect(() => {
    if (chatOpen) setUnreadCount(0);
  }, [chatOpen]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !currentRoom) return;

    setSending(true);
    try {
      const response = await api.post(`/chat/messages/${currentRoom.id}`, {
        message: newMessage.trim(),
        room_id: currentRoom.id
      });
      if (response.data) {
        setMessages(prev => [...prev, response.data]);
      }
      setNewMessage('');
    } catch (err) {
      toast.error('Błąd wysyłania wiadomości');
    } finally {
      setSending(false);
    }
  };

  const handleArchive = async () => {
    if (!isAdmin || !currentRoom) return;
    if (!window.confirm(`Zarchiwizować czat "${currentRoom.name}" z dnia ${currentDate}?`)) return;

    setArchiving(true);
    try {
      await api.post(`/chat/archive/${currentDate}?room_id=${currentRoom.id}`);
      toast.success('Czat zarchiwizowany');
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd archiwizacji');
    } finally {
      setArchiving(false);
    }
  };

  const selectRoom = (room) => {
    setCurrentRoom(room);
    setMessages([]);
    setLoading(true);
  };

  const toggleMember = (userId) => {
    setSelectedMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) {
      toast.error('Podaj nazwę pokoju');
      return;
    }
    if (selectedMembers.length === 0) {
      toast.error('Wybierz przynajmniej jednego pracownika');
      return;
    }

    setCreatingRoom(true);
    try {
      // Determine room type based on member count
      const roomType = selectedMembers.length === 1 ? 'direct' : 'channel';
      const members = [user.id, ...selectedMembers];
      
      const response = await api.post('/chat/rooms', {
        name: newRoomName.trim(),
        room_type: roomType,
        icon: newRoomColor.icon,
        members: members,
        description: `Utworzony przez ${user.full_name}`
      });
      
      toast.success(`Pokój "${newRoomName}" utworzony!`);
      
      // Reset form
      setNewRoomName('');
      setNewRoomColor(ROOM_COLORS[0]);
      setSelectedMembers([]);
      setShowNewRoomDialog(false);
      
      // Refresh rooms and select new one
      await fetchRooms();
      if (response.data) {
        setCurrentRoom(response.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd tworzenia pokoju');
    } finally {
      setCreatingRoom(false);
    }
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  };

  const getRoleBadge = (role) => {
    const colors = { admin: 'bg-amber-100 text-amber-700', kierownik: 'bg-blue-100 text-blue-700' };
    const labels = { admin: 'Szef', kierownik: 'Kier.' };
    if (!labels[role]) return null;
    return <Badge className={`${colors[role]} text-[9px] ml-1 px-1 py-0`}>{labels[role]}</Badge>;
  };

  if (!user) return null;

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setChatOpen(!chatOpen)}
        className={`fixed bottom-20 right-6 z-[9999] w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-105 ${
          chatOpen ? 'bg-slate-600 hover:bg-slate-700' : 'bg-gradient-to-br from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700'
        }`}
        data-testid="chat-toggle-btn"
      >
        {chatOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6 text-navy-800" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </>
        )}
      </button>

      {/* Chat Panel */}
      <div
        className={`fixed bottom-36 right-6 z-[9998] w-[520px] max-w-[calc(100vw-3rem)] h-[520px] bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 origin-bottom-right ${
          chatOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'
        }`}
        data-testid="chat-panel"
      >
        <div className="flex h-full">
          {/* Sidebar - Rooms */}
          <div className="w-48 bg-navy-800 flex flex-col">
            {/* Header */}
            <div className="p-3 border-b border-navy-700">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-gold-400" />
                  Chat firmowy
                </h3>
                <button 
                  onClick={() => setShowNewRoomDialog(true)}
                  className="w-6 h-6 bg-gold-500 hover:bg-gold-600 rounded-full flex items-center justify-center transition-colors"
                  title="Nowy pokój"
                  data-testid="new-room-btn"
                >
                  <Plus className="w-4 h-4 text-navy-800" />
                </button>
              </div>
              <p className="text-navy-400 text-[10px] mt-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {currentDate}
              </p>
            </div>

            {/* Channels */}
            <ScrollArea className="flex-1">
              <div className="p-2">
                <p className="text-navy-400 text-[10px] uppercase font-semibold px-2 py-1">Kanały</p>
                {rooms.channels.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => selectRoom(room)}
                    className={`w-full text-left px-2 py-1.5 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                      currentRoom?.id === room.id
                        ? 'bg-gold-500/20 text-gold-300'
                        : 'text-navy-300 hover:bg-navy-700 hover:text-white'
                    }`}
                  >
                    <span>{room.icon || <Hash className="w-3 h-3" />}</span>
                    <span className="truncate flex-1 text-xs">{room.name}</span>
                    {room.today_count > 0 && (
                      <Badge className="bg-navy-600 text-[9px] px-1">{room.today_count}</Badge>
                    )}
                  </button>
                ))}

                <p className="text-navy-400 text-[10px] uppercase font-semibold px-2 py-1 mt-3">Rozmowy</p>
                {rooms.direct_messages.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => selectRoom(room)}
                    className={`w-full text-left px-2 py-1.5 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                      currentRoom?.id === room.id
                        ? 'bg-gold-500/20 text-gold-300'
                        : 'text-navy-300 hover:bg-navy-700 hover:text-white'
                    }`}
                  >
                    <span>{room.icon || <User className="w-3 h-3" />}</span>
                    <span className="truncate flex-1 text-xs">{room.name}</span>
                    {room.today_count > 0 && (
                      <Badge className="bg-emerald-600 text-[9px] px-1">{room.today_count}</Badge>
                    )}
                  </button>
                ))}

                {/* Available users for quick DM */}
                {rooms.users?.filter(u => u.id !== user.id).length > 0 && (
                  <>
                    <p className="text-navy-400 text-[10px] uppercase font-semibold px-2 py-1 mt-3">Pracownicy</p>
                    {rooms.users.filter(u => u.id !== user.id).slice(0, 5).map((u) => (
                      <button
                        key={u.id}
                        onClick={() => {
                          const dmId = `dm_${[user.id, u.id].sort().join('_')}`;
                          selectRoom({ id: dmId, name: u.full_name, room_type: 'direct', icon: '👤' });
                        }}
                        className="w-full text-left px-2 py-1.5 rounded-lg text-xs flex items-center gap-2 text-navy-400 hover:bg-navy-700 hover:text-white transition-colors"
                      >
                        <span className="w-5 h-5 bg-navy-600 rounded-full flex items-center justify-center text-[10px]">
                          {u.full_name?.charAt(0)}
                        </span>
                        <span className="truncate">{u.full_name}</span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </ScrollArea>

            {/* Footer actions */}
            <div className="p-2 border-t border-navy-700 flex gap-1">
              <Link
                to="/chat-history"
                onClick={() => setChatOpen(false)}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-navy-700 hover:bg-navy-600 rounded text-[10px] text-navy-300 hover:text-white transition-colors"
              >
                <History className="w-3 h-3" />
                Historia
              </Link>
              {isAdmin && currentRoom && (
                <button
                  onClick={handleArchive}
                  disabled={archiving || messages.length === 0}
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 rounded text-[10px] text-amber-400 disabled:opacity-50 transition-colors"
                >
                  {archiving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
                  Archiwizuj
                </button>
              )}
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="flex-1 flex flex-col bg-slate-50">
            {/* New Room Dialog */}
            {showNewRoomDialog ? (
              <div className="flex-1 flex flex-col p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-gold-500" />
                    Nowy pokój czatu
                  </h3>
                  <button 
                    onClick={() => setShowNewRoomDialog(false)}
                    className="w-6 h-6 bg-slate-200 hover:bg-slate-300 rounded-full flex items-center justify-center"
                  >
                    <X className="w-4 h-4 text-slate-600" />
                  </button>
                </div>

                {/* Room Name */}
                <div className="mb-4">
                  <Label className="text-xs font-medium text-slate-600 mb-1 block">Nazwa pokoju</Label>
                  <Input
                    value={newRoomName}
                    onChange={(e) => setNewRoomName(e.target.value)}
                    placeholder="np. Egipt 2026, Pilne sprawy..."
                    className="h-9"
                    data-testid="new-room-name-input"
                  />
                </div>

                {/* Color/Icon Selection */}
                <div className="mb-4">
                  <Label className="text-xs font-medium text-slate-600 mb-2 block">Kolor / Ikona</Label>
                  <div className="grid grid-cols-6 gap-2">
                    {ROOM_COLORS.map((color) => (
                      <button
                        key={color.id}
                        onClick={() => setNewRoomColor(color)}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg transition-all ${
                          newRoomColor.id === color.id
                            ? 'ring-2 ring-gold-500 ring-offset-2 scale-110'
                            : 'hover:scale-105'
                        } ${color.color}`}
                        title={color.name}
                      >
                        {color.icon}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Wybrany: {newRoomColor.icon} {newRoomColor.name}</p>
                </div>

                {/* Member Selection */}
                <div className="flex-1 mb-4">
                  <Label className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1.5">
                    <Users className="w-[18px] h-[18px] text-navy-600 flex-shrink-0" />
                    <span>Wybierz pracowników</span>
                    {selectedMembers.length > 0 ? (
                      <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gold-100 text-gold-700 border border-gold-200">
                        {selectedMembers.length} {selectedMembers.length === 1 ? 'wybrany' : 'wybranych'}
                      </span>
                    ) : (
                      <span className="ml-1 text-slate-400 text-[10px]">(0 wybranych)</span>
                    )}
                  </Label>
                  <ScrollArea className="h-32 border border-slate-200 rounded-lg p-2">
                    {rooms.users?.filter(u => u.id !== user.id).length > 0 ? (
                      rooms.users.filter(u => u.id !== user.id).map((u) => (
                        <div 
                          key={u.id}
                          onClick={() => toggleMember(u.id)}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            selectedMembers.includes(u.id)
                              ? 'bg-gold-100 border border-gold-300'
                              : 'hover:bg-slate-100'
                          }`}
                        >
                          <Checkbox 
                            checked={selectedMembers.includes(u.id)}
                            onCheckedChange={() => toggleMember(u.id)}
                          />
                          <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-xs font-semibold">
                            {u.full_name?.charAt(0)}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-slate-800">{u.full_name}</p>
                            <p className="text-[10px] text-slate-500">{u.role}</p>
                          </div>
                          {selectedMembers.includes(u.id) && (
                            <Check className="w-4 h-4 text-gold-600" />
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-center py-4">
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mb-2">
                          <Plus className="w-5 h-5 text-slate-400" />
                        </div>
                        <p className="text-slate-500 text-sm">Brak innych pracowników</p>
                        <p className="text-slate-400 text-xs">Dodaj pracowników w panelu Użytkownicy</p>
                      </div>
                    )}
                  </ScrollArea>
                </div>

                {/* Create Button */}
                <Button
                  onClick={handleCreateRoom}
                  disabled={creatingRoom || !newRoomName.trim() || selectedMembers.length === 0}
                  className="w-full bg-gold-500 hover:bg-gold-600 text-navy-800"
                  data-testid="create-room-btn"
                >
                  {creatingRoom ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Tworzenie...</>
                  ) : (
                    <><Plus className="w-4 h-4 mr-2" /> Utwórz pokój</>
                  )}
                </Button>
              </div>
            ) : (
              <>
                {/* Room Header */}
                {currentRoom && (
                  <div className="px-4 py-2 bg-white border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{currentRoom.icon}</span>
                      <div>
                        <h4 className="font-semibold text-slate-800 text-sm">{currentRoom.name}</h4>
                        <p className="text-[10px] text-slate-400">{messages.length} wiadomości dziś</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
                      <span className={`text-[10px] ${wsConnected ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {wsConnected ? 'realtime' : 'polling'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Messages */}
                <ScrollArea className="flex-1 p-3">
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : !currentRoom ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <MessageCircle className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500 text-sm">Wybierz pokój z listy</p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <MessageCircle className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500 text-sm">Brak wiadomości</p>
                      <p className="text-slate-400 text-xs">Napisz pierwszą wiadomość!</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {hasMore && (
                        <div className="text-center py-1">
                          <button
                            onClick={loadMore}
                            disabled={loadingMore}
                            className="text-xs text-navy-600 hover:text-navy-800 bg-white border border-navy-200 px-3 py-1 rounded-full disabled:opacity-50 transition-colors"
                            data-testid="load-more-messages"
                          >
                            {loadingMore ? 'Ładowanie...' : 'Załaduj więcej'}
                          </button>
                        </div>
                      )}
                      {messages.map((msg) => {
                        const isOwn = msg.sender_id === user.id;
                        return (
                          <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%]`}>
                              {!isOwn && (
                                <p className="text-[10px] text-slate-500 mb-0.5 ml-1 flex items-center">
                                  {msg.sender_name}
                                  {getRoleBadge(msg.sender_role)}
                                </p>
                              )}
                              <div className={`px-3 py-1.5 rounded-2xl ${
                                isOwn
                                  ? 'bg-gold-500 text-navy-800 rounded-br-md'
                                  : 'bg-white text-slate-800 shadow-sm rounded-bl-md'
                              }`}>
                                <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                              </div>
                              <p className={`text-[9px] text-slate-400 mt-0.5 ${isOwn ? 'text-right mr-1' : 'ml-1'}`}>
                                {formatTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Input */}
                {currentRoom && (
                  <form onSubmit={handleSend} className="p-3 bg-white border-t border-slate-100">
                    <div className="flex gap-2">
                      <Input
                        ref={inputRef}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={`Wiadomość do ${currentRoom.name}...`}
                        className="flex-1 h-9 text-sm border-slate-200"
                        disabled={sending}
                        data-testid="chat-input"
                      />
                      <Button
                        type="submit"
                        size="icon"
                        disabled={sending || !newMessage.trim()}
                        className="h-9 w-9 bg-gold-500 hover:bg-gold-600 text-navy-800"
                        data-testid="chat-send-btn"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
